import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { map, distinctUntilChanged } from 'rxjs/operators';

export interface NavigationState {
  isLoading: boolean;
  featuredPageTitle: string | null;
  featuredPageSlug: string | null;
  isAllowFeaturedPageView: boolean;
  companySlug: string | null;
  currentUrl: string;
}

@Injectable({
  providedIn: 'root'
})
export class NavigationStateService {
  private _isLoading$ = new BehaviorSubject<boolean>(false);
  private _featuredPageTitle$ = new BehaviorSubject<string | null>(null);
  private _featuredPageSlug$ = new BehaviorSubject<string | null>(null);
  private _isAllowFeaturedPageView$ = new BehaviorSubject<boolean>(false);
  private _companySlug$ = new BehaviorSubject<string | null>(null);
  private _currentUrl$ = new BehaviorSubject<string>('');

  // Public observables
  public readonly isLoading$ = this._isLoading$.asObservable();
  public readonly featuredPageTitle$ = this._featuredPageTitle$.asObservable();
  public readonly featuredPageSlug$ = this._featuredPageSlug$.asObservable();
  public readonly isAllowFeaturedPageView$ = this._isAllowFeaturedPageView$.asObservable();
  public readonly companySlug$ = this._companySlug$.asObservable();
  public readonly currentUrl$ = this._currentUrl$.asObservable();

  // Combined navigation state
  public readonly navigationState$: Observable<NavigationState> = combineLatest([
    this.isLoading$,
    this.featuredPageTitle$,
    this.featuredPageSlug$,
    this.isAllowFeaturedPageView$,
    this.companySlug$,
    this.currentUrl$
  ]).pipe(
    map(([isLoading, featuredPageTitle, featuredPageSlug, isAllowFeaturedPageView, companySlug, currentUrl]) => ({
      isLoading,
      featuredPageTitle,
      featuredPageSlug,
      isAllowFeaturedPageView,
      companySlug,
      currentUrl
    })),
    distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr))
  );

  // Setters
  setLoading(isLoading: boolean): void {
    this._isLoading$.next(isLoading);
  }

  setFeaturedPageTitle(title: string | null): void {
    this._featuredPageTitle$.next(title);
  }

  setFeaturedPageSlug(slug: string | null): void {
    this._featuredPageSlug$.next(slug);
  }

  setIsAllowFeaturedPageView(isAllowed: boolean): void {
    this._isAllowFeaturedPageView$.next(isAllowed);
  }

  setCompanySlug(slug: string | null): void {
    this._companySlug$.next(slug);
  }

  setCurrentUrl(url: string): void {
    this._currentUrl$.next(url);
  }

  // Getters for current values
  get currentNavigationState(): NavigationState {
    return {
      isLoading: this._isLoading$.value,
      featuredPageTitle: this._featuredPageTitle$.value,
      featuredPageSlug: this._featuredPageSlug$.value,
      isAllowFeaturedPageView: this._isAllowFeaturedPageView$.value,
      companySlug: this._companySlug$.value,
      currentUrl: this._currentUrl$.value
    };
  }

  // Helper methods
  isFeaturedPageRoute(url: string): boolean {
    return url.includes('/featured/') || url.includes('/myspotlight/');
  }

  isPublicFeaturedPageRoute(url: string): boolean {
    return url.includes('/c/') && (url.includes('/featured/') || url.includes('/myspotlight/'));
  }

  isPrivateFeaturedPageRoute(url: string): boolean {
    return url.includes('/company/') && (url.includes('/featured/') || url.includes('/myspotlight/'));
  }

  // Reset state
  reset(): void {
    this._isLoading$.next(false);
    this._featuredPageTitle$.next(null);
    this._featuredPageSlug$.next(null);
    this._isAllowFeaturedPageView$.next(false);
    this._companySlug$.next(null);
    this._currentUrl$.next('');
  }
}
